import { useState, useEffect } from "react";
import { recipesApi } from "../api/recipes";
import { Recipe, Ingredient } from "../types";
import { useAuthContext } from "../contexts/AuthContext";

/**
 * useUserContributions hook
 *
 * This custom hook fetches and manages the user's contributions (recipes and ingredients).
 * It relies on the authentication context to get the current user's ID and authentication status.
 *
 * @returns An object containing:
 * - recipes: An array of Recipe objects contributed by the user.
 * - ingredients: An array of Ingredient objects contributed by the user.
 * - loadingContributions: A boolean indicating whether the contributions are currently being loaded.
 */
export function useUserContributions() {
  // Use the authentication context to get the current user and their authentication status.
  // The user object contains the user's full profile, so we don't need to fetch it again.
  const { user, isAuthenticated } = useAuthContext();

  // State to hold the fetched recipes and ingredients
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);

  // State to track the loading status of the contributions
  const [loadingContributions, setLoadingContributions] = useState(true);

  // useEffect hook to fetch contributions whenever the user's authentication
  // status or user ID changes.
  useEffect(() => {
    /**
     * Async function to fetch user's contributions from the API.
     */
    const fetchContributions = async () => {
      // Only proceed if the user is authenticated and their ID is available.
      if (isAuthenticated && user?._id) {
        try {
          setLoadingContributions(true);
          // Call the API to get contributions for the current user's ID.
          const response = await recipesApi.getUserContributions(user._id);

          // Update the state with the fetched data, defaulting to an empty array
          // if the response is null or undefined.
          setRecipes(response.recipes || []);
          setIngredients(response.ingredients || []);
        } catch (err) {
          // If an error occurs, log it and set the state to empty arrays.
          console.error("Failed to fetch user contributions:", err);
          setRecipes([]);
          setIngredients([]);
        } finally {
          // Set loading to false once the fetch operation is complete (success or failure).
          setLoadingContributions(false);
        }
      } else {
        // If not authenticated or user ID is missing, reset the state and stop loading.
        setRecipes([]);
        setIngredients([]);
        setLoadingContributions(false);
      }
    };

    fetchContributions();
  }, [isAuthenticated, user?._id]); // Dependencies for the useEffect hook.

  // Return the contributions data and loading status for use in components.
  return {
    recipes,
    ingredients,
    loadingContributions,
  };
}
